import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  DialogContent,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Tooltip,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import WavesIcon from '@mui/icons-material/Waves';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { GGDialog, GGDialogTitle } from './StyledDialogs';
import { TwoColumnLayout, LeftColumn, RightColumn } from './StyledComponents';
import TitleSubtitleChip from './TitleSubtitleChip';
import MonsterTooltipContent from './MonsterTooltipContent';
import { getMonstersByCRRange, getAvailableEnvironments } from '../../services/monsterService';
import { generateEncountersOfType, getCRRangeForLevel, calculateEncounterBudget, getEncounterKey } from '../../utils/encounterUtils';
import {
  clampDmPartySize,
  clampDmPartyLevel,
  loadDmPartyDefaults,
  saveDmPartyDefaults,
} from '../../utils/dmPartyDefaults';
import EncounterDifficultyBar from './EncounterDifficultyBar';
import { ROLES } from '../../data/roles';

const FormContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const FormRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));


const DifficultyToggleGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  width: '100%',
  '& .MuiToggleButton-root': {
    flex: 1,
    fontFamily: 'MedievalSharp',
    fontSize: '1rem',
    fontWeight: 600,
    padding: theme.spacing(1.5),
    borderColor: theme.palette.background.dark,
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

const DifficultyButton = styled(ToggleButton)(({ theme, difficulty }) => {
  const colors = {
    easy: '#4CAF50',
    medium: '#FF9800',
    hard: '#F44336',
  };
  return {
    '&.Mui-selected': {
      backgroundColor: colors[difficulty] || theme.palette.primary.main,
      '&:hover': {
        backgroundColor: colors[difficulty] || theme.palette.primary.dark,
        filter: 'brightness(0.9)',
      },
    },
  };
});

const EncounterTypeToggleGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  width: '100%',
  '& .MuiToggleButton-root': {
    flex: 1,
    fontFamily: 'MedievalSharp',
    fontSize: '0.9rem',
    fontWeight: 600,
    padding: theme.spacing(1),
    borderColor: theme.palette.background.dark,
    color: theme.palette.text.secondary,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

const EncounterTypeLabel = styled(Typography)({
  fontSize: '0.7rem',
  opacity: 0.8,
  textAlign: 'center',
  fontFamily: 'Merriweather',
});

const GenerateButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontSize: '1.2rem',
  fontFamily: 'MedievalSharp',
  fontWeight: 600,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const EncounterCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  border: `2px solid ${theme.palette.background.dark}`,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
    '& .add-hint': {
      opacity: 1,
    },
  },
}));

const EncounterHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const EncounterHeaderLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const EncounterTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'MedievalSharp',
  fontSize: '1.1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const MonsterList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

const MonsterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1),
  backgroundColor: theme.palette.background.light,
  borderRadius: theme.shape.borderRadius / 2,
  cursor: 'default',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.background.main,
  },
}));

const MonsterThumbnail = styled('img')(({ theme }) => ({
  width: 28,
  height: 28,
  borderRadius: 4,
  objectFit: 'cover',
  backgroundColor: theme.palette.background.dark,
  flexShrink: 0,
}));

const MonsterNameContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
}));

const MonsterCount = styled(Typography)(({ theme }) => ({
  fontFamily: 'Merriweather',
  fontSize: '0.85rem',
  fontWeight: 700,
  color: theme.palette.text.secondary,
}));

const MonsterName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Merriweather',
  fontSize: '0.85rem',
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const RoleBadge = styled('span')(({ theme, roleColor }) => ({
  fontSize: '0.75rem',
  opacity: 0.9,
  marginLeft: theme.spacing(1),
  color: roleColor || theme.palette.text.secondary,
}));

const MonsterStats = styled(Typography)(({ theme }) => ({
  fontFamily: 'Merriweather',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
}));

const XPBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.25, 1),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.7rem',
  fontFamily: 'Merriweather',
  fontWeight: 600,
}));

const EncounterFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(1.5),
  paddingTop: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.background.dark}`,
}));

const AddToTokensHint = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.primary.main,
  fontSize: '0.75rem',
  fontFamily: 'Merriweather',
  fontWeight: 600,
  opacity: 0.8,
  transition: 'opacity 0.2s ease',
  '.MuiSvgIcon-root': {
    fontSize: '1rem',
  },
}));

const ResultsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const ScrollContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingTop: theme.spacing(1),
  paddingRight: theme.spacing(1),
  marginRight: -theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.background.dark,
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 4,
  },
}));

const GenerateMoreButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontFamily: 'Merriweather',
  fontWeight: 600,
}));

const ANY_ENVIRONMENT = '__any__';

const ENCOUNTER_TYPE_LABELS = {
  boss: 'A boss or boss + minions',
  ambush: 'Small group of coordinated enemies',
  waves: 'Large horde of weaker enemies',
};

const EncounterGeneratorModal = ({ open, onClose, onSelectEncounter, partyDefaults, onPartyDefaultsChange }) => {
  const [partySize, setPartySize] = useState('4');
  const [partyLevel, setPartyLevel] = useState('1');
  const [difficulty, setDifficulty] = useState('medium');
  const [encounterType, setEncounterType] = useState('ambush');
  const [selectedEnvironments, setSelectedEnvironments] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [targetXP, setTargetXP] = useState(null);
  const [generationMessage, setGenerationMessage] = useState(null);
  const seenEncounterKeys = useRef(new Set());

  const environments = useMemo(() => getAvailableEnvironments(), []);

  const effectivePartySize = useMemo(
    () => Math.max(1, Math.min(10, parseInt(partySize, 10) || 4)),
    [partySize]
  );
  const effectivePartyLevel = useMemo(
    () => Math.max(1, Math.min(20, parseInt(partyLevel, 10) || 1)),
    [partyLevel]
  );

  useEffect(() => {
    if (!open) return;
    const src = partyDefaults ?? loadDmPartyDefaults();
    setPartySize(String(src.partySize));
    setPartyLevel(String(src.partyLevel));
  }, [open, partyDefaults?.partySize, partyDefaults?.partyLevel]);

  const persistPartyFromFields = () => {
    const size = clampDmPartySize(parseInt(partySize, 10));
    const level = clampDmPartyLevel(parseInt(partyLevel, 10));
    setPartySize(String(size));
    setPartyLevel(String(level));
    if (onPartyDefaultsChange) {
      onPartyDefaultsChange({ partySize: size, partyLevel: level });
    } else {
      saveDmPartyDefaults({ partySize: size, partyLevel: level });
    }
    return { partySize: size, partyLevel: level };
  };

  const handleDifficultyChange = (event, newDifficulty) => {
    if (newDifficulty !== null) {
      setDifficulty(newDifficulty);
      setGenerationMessage(null);
    }
  };

  const handleEncounterTypeChange = (event, newType) => {
    if (newType !== null) {
      setEncounterType(newType);
      setEncounters([]);
      setTargetXP(null);
      setGenerationMessage(null);
      seenEncounterKeys.current = new Set();
    }
  };

  const handleEnvironmentChange = (event) => {
    const value = event.target.value;
    
    // If "Any Environment" is selected, clear the selection
    if (value.includes(ANY_ENVIRONMENT)) {
      setSelectedEnvironments([]);
      return;
    }
    
    setSelectedEnvironments(typeof value === 'string' ? value.split(',') : value);
  };

  const handleDeleteEnvironment = (envToDelete) => {
    setSelectedEnvironments(prev => prev.filter(env => env !== envToDelete));
  };

  const generateEncounters = (append = false, partyOverride = null) => {
    const ps = partyOverride?.partySize ?? effectivePartySize;
    const pl = partyOverride?.partyLevel ?? effectivePartyLevel;
    const levels = Array(ps).fill(pl);
    const { min, max } = getCRRangeForLevel(pl);
    
    const envFilter = selectedEnvironments.length > 0 ? selectedEnvironments : null;
    const monsters = getMonstersByCRRange(min, max, envFilter);

    if (monsters.length === 0) {
      if (!append) {
        seenEncounterKeys.current = new Set();
        setEncounters([]);
        setTargetXP(null);
      }
      setGenerationMessage({
        severity: 'error',
        text:
          'No monsters match this party level range and environment filters. Clear environments or pick a wider set.',
      });
      return;
    }
    
    // Clear seen keys if not appending
    if (!append) {
      seenEncounterKeys.current = new Set();
    }
    
    const newEncounters = generateEncountersOfType(
      monsters, 
      levels, 
      difficulty, 
      encounterType, 
      3, 
      seenEncounterKeys.current
    );
    
    // Add new encounter keys to the seen set
    newEncounters.forEach(enc => {
      const key = getEncounterKey(enc);
      if (key) seenEncounterKeys.current.add(key);
    });
    
    const budget = calculateEncounterBudget(levels, difficulty);
    
    setTargetXP(budget);
    setEncounters(prev => append ? [...prev, ...newEncounters] : newEncounters);

    const requested = 3;
    if (newEncounters.length === 0) {
      setGenerationMessage({
        severity: 'error',
        text: append
          ? 'No additional unique encounters matched these settings. Try Generate (fresh), another type, or different filters.'
          : 'No valid encounters could be built for this type and difficulty. Try another difficulty, encounter type, or environment.',
      });
    } else if (newEncounters.length < requested) {
      setGenerationMessage({
        severity: 'warning',
        text: append
          ? `Only ${newEncounters.length} new unique encounter${newEncounters.length === 1 ? '' : 's'} (requested ${requested}). Try again or adjust filters.`
          : `Only ${newEncounters.length} unique encounter${newEncounters.length === 1 ? '' : 's'} were found (requested ${requested}). Click Generate again for more variety.`,
      });
    } else {
      setGenerationMessage(null);
    }
    
    console.log('=== Generated Encounters ===');
    console.log('Type:', encounterType);
    console.log('Count:', newEncounters.length);
    console.log('Total unique:', seenEncounterKeys.current.size);
    console.log('Encounters:', newEncounters);
    console.log('============================');
  };

  const handleGenerate = () => {
    const nextParty = persistPartyFromFields();
    generateEncounters(false, nextParty);
  };

  const handleGenerateMore = () => {
    const nextParty = persistPartyFromFields();
    generateEncounters(true, nextParty);
  };

  const handleSelectEncounter = (encounter) => {
    console.log('Selected encounter:', encounter);
    if (onSelectEncounter) {
      onSelectEncounter(encounter);
    }
  };

  const renderEncounterCard = (encounter) => {
    if (!encounter) return null;
    
    return (
      <EncounterCard onClick={() => handleSelectEncounter(encounter)}>
        <EncounterHeader>
          <EncounterHeaderLeft>
            <EncounterTitle>
              {encounter.factionLabel || encounter.label}
            </EncounterTitle>

            {encounter.tactic && (
              <TitleSubtitleChip
                icon={<CrisisAlertIcon fontSize="small" />}
                title={encounter.tactic}
                tooltip={encounter.tacticDescription || encounter.tactic}
                tooltipProps={{ placement: 'top', arrow: true }}
                chipProps={{
                  variant: 'outlined',
                  color: 'secondary',
                  sx: { ml: 1 },
                }}
              />
            )}
          </EncounterHeaderLeft>
          <EncounterDifficultyBar
            width="200px"
            encounter={encounter}
            partySize={effectivePartySize}
            partyLevel={effectivePartyLevel}
          />
        </EncounterHeader>
        <MonsterList>
          {encounter.monsters.map((monster, idx) => {
            const roleInfo = ROLES[monster.role] || ROLES.soldier;
            
            return (
              <Tooltip 
                key={`${monster.index}-${idx}`}
                title={<MonsterTooltipContent monster={monster} />}
                placement="left"
                arrow
                enterDelay={300}
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'background.light',
                      border: '1px solid',
                      borderColor: 'background.dark',
                      boxShadow: 4,
                      p: 0,
                    },
                  },
                  arrow: {
                    sx: {
                      color: 'background.light',
                    },
                  },
                }}
              >
                <MonsterRow>
                  <MonsterNameContainer>
                    {monster.img && (
                      <MonsterThumbnail src={monster.img} alt={monster.name} />
                    )}
                    <MonsterName>
                      {monster.name}
                      <MonsterCount>{monster.count > 1 ? `(${monster.count})` : ''}</MonsterCount>
                      
                      <RoleBadge roleColor={roleInfo.color} title={roleInfo.description}>
                        {roleInfo.icon} {monster.role}
                      </RoleBadge>
                      
                    </MonsterName>
                  </MonsterNameContainer>
                  <MonsterStats>
                    CR {monster.cr} • {monster.xp * monster.count} XP
                  </MonsterStats>
                </MonsterRow>
              </Tooltip>
            );
          })}
        </MonsterList>
        <EncounterFooter>
          <XPBadge>
            Adjusted XP: {encounter.adjustedXP.toLocaleString()}
          </XPBadge>
          <AddToTokensHint className="add-hint">
            <AddCircleOutlineIcon />
            Add to Tokens
          </AddToTokensHint>
        </EncounterFooter>
      </EncounterCard>
    );
  };

  return (
    <GGDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <GGDialogTitle>Encounter Generator</GGDialogTitle>
      <DialogContent sx={{ overflow: 'hidden', pb: 3 }}>
        <TwoColumnLayout sx={{ pt: 3 }}>
          <LeftColumn>
            <FormContainer>
              <FormRow>
                <TextField
                  label="Party Size"
                  type="number"
                  value={partySize}
                  onChange={(e) => setPartySize(e.target.value)}
                  onBlur={persistPartyFromFields}
                  inputProps={{ min: 1, max: 10 }}
                  fullWidth
                />
              </FormRow>

              <FormRow>
                <TextField
                  label="Party Level"
                  type="number"
                  value={partyLevel}
                  onChange={(e) => setPartyLevel(e.target.value)}
                  onBlur={persistPartyFromFields}
                  inputProps={{ min: 1, max: 20 }}
                  fullWidth
                />
              </FormRow>

              <FormRow>
                <DifficultyToggleGroup
                  value={difficulty}
                  exclusive
                  onChange={handleDifficultyChange}
                >
                  <DifficultyButton value="easy" difficulty="easy">
                    Easy
                  </DifficultyButton>
                  <DifficultyButton value="medium" difficulty="medium">
                    Medium
                  </DifficultyButton>
                  <DifficultyButton value="hard" difficulty="hard">
                    Hard
                  </DifficultyButton>
                </DifficultyToggleGroup>
              </FormRow>

              <FormRow>
                <EncounterTypeToggleGroup
                  value={encounterType}
                  exclusive
                  onChange={handleEncounterTypeChange}
                >
                  
                  <ToggleButton value="ambush">
                    <GroupsIcon fontSize="medium" />
                    Ambush
                  </ToggleButton>
                  <ToggleButton value="waves">
                    <WavesIcon fontSize="medium" />
                    Waves
                  </ToggleButton>
                  <ToggleButton value="boss">
                    <PersonIcon fontSize="medium" />
                    Boss
                  </ToggleButton>
                  
                </EncounterTypeToggleGroup>

                <EncounterTypeLabel>{ENCOUNTER_TYPE_LABELS[encounterType]}</EncounterTypeLabel>
              </FormRow>

              <FormRow>
                <FormControl fullWidth>
                  <InputLabel>Environments</InputLabel>
                  <Select
                    multiple
                    value={selectedEnvironments}
                    label="Environment"
                    onChange={handleEnvironmentChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.length === 0 ? (
                          <Typography sx={{ fontFamily: 'Merriweather', color: 'text.secondary', fontStyle: 'italic' }}>
                            Any Environment
                          </Typography>
                        ) : (
                          selected.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                              onDelete={() => handleDeleteEnvironment(value)}
                              onMouseDown={(event) => event.stopPropagation()}
                              sx={{ textTransform: 'capitalize', fontSize: '0.75rem', height: 24 }}
                            />
                          ))
                        )}
                      </Box>
                    )}
                  >
                    <MenuItem value={ANY_ENVIRONMENT}>
                      <em>Any Environment (clear selection)</em>
                    </MenuItem>
                    {environments.map((env) => (
                      <MenuItem key={env} value={env} sx={{ textTransform: 'capitalize' }}>
                        <Checkbox checked={selectedEnvironments.includes(env)} />
                        <ListItemText 
                          primary={env} 
                          primaryTypographyProps={{ 
                            sx: { textTransform: 'capitalize' } 
                          }} 
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FormRow>
            </FormContainer>

            <GenerateButton
              onClick={handleGenerate}
              startIcon={<AutoFixHighIcon />}
              fullWidth
            >
              Generate
            </GenerateButton>
          </LeftColumn>

          <RightColumn>
            {generationMessage && (
              <Alert
                severity={generationMessage.severity}
                onClose={() => setGenerationMessage(null)}
                sx={{ mb: 2, fontFamily: 'Merriweather' }}
              >
                {generationMessage.text}
              </Alert>
            )}
            {encounters.length > 0 ? (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ fontFamily: 'Merriweather', color: 'text.secondary', mb: 2 }}
                >
                  Target XP: {targetXP?.toLocaleString()}
                  {selectedEnvironments.length > 0 && ` • ${selectedEnvironments.join(', ')}`}
                  {' • Click to select'}
                </Typography>
                <ScrollContainer>
                  <ResultsContainer>
                    {encounters.map((encounter, idx) => (
                      <Box key={idx}>
                        {renderEncounterCard(encounter)}
                      </Box>
                    ))}
                  </ResultsContainer>
                  <GenerateMoreButton
                    variant="outlined"
                    onClick={handleGenerateMore}
                    startIcon={<RefreshIcon />}
                    fullWidth
                  >
                    Generate More
                  </GenerateMoreButton>
                </ScrollContainer>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'text.secondary',
                }}
              >
                <Typography
                  sx={{ fontFamily: 'Merriweather', fontStyle: 'italic', textAlign: 'center', px: 2 }}
                >
                  {generationMessage?.severity === 'error'
                    ? 'Adjust settings and try Generate again.'
                    : 'Configure your party and click Generate'}
                </Typography>
              </Box>
            )}
          </RightColumn>
        </TwoColumnLayout>
      </DialogContent>
    </GGDialog>
  );
};

export default EncounterGeneratorModal;
